// ----------------------------------------------------------------------------
//  SolarProposalReceipt.jsx – recibo / PDF stand-alone
// ----------------------------------------------------------------------------
import React, {
  forwardRef,
  useState,
  useRef,
  useEffect,
  useImperativeHandle,
} from "react";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import { useTheme, useMediaQuery } from "@mui/material";
import logo from "../../assets/logo/logo-oficial.png";

/* ───────── helper formato ───────── */
const fmt = (v, { currency = false } = {}) =>
  v == null || v === "-"
    ? "-"
    : Number(v).toLocaleString(
        "es-MX",
        currency ? { style: "currency", currency: "MXN" } : {}
      );

/* ───────── fila de tabla ───────── */
const Row = ({ label, value }) => (
  <tr>
    <td style={{ border: "1px solid #000", padding: 8 }}>{label}</td>
    <td
      style={{
        border: "1px solid #000",
        padding: 8,
        textAlign: "center",
        fontWeight: 600,
      }}
    >
      {value}
    </td>
  </tr>
);

/* ───────── componente principal ───────── */
const SolarProposalReceipt = forwardRef(({ data, hideActions }, ref) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const divRef = useRef(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  /* expone método download() al padre */
  useImperativeHandle(ref, () => ({
    async download() {
      if (!divRef.current) return;
      try {
        setLoading(true);
        const canvas = await html2canvas(divRef.current, {
          scale: 2,
          useCORS: true,
        });
        const img = canvas.toDataURL("image/png");
        const pdf = new jsPDF("portrait", "pt", "letter");
        const { width: pw, height: ph } = pdf.internal.pageSize;
        const ratio = Math.min(pw / canvas.width, ph / canvas.height);
        pdf.addImage(
          img,
          "PNG",
          (pw - canvas.width * ratio) / 2,
          0,
          canvas.width * ratio,
          canvas.height * ratio
        );
        pdf.save(`CotizacionSolar-${data?.titular || "cliente"}.pdf`);
      } catch (e) {
        setError("No se pudo generar el PDF: " + e.message);
      } finally {
        setLoading(false);
      }
    },
  }));

  /* reinicia error si cambian datos */
  useEffect(() => setError(""), [data]);

  /* estados de carga / error */
  if (!data) return null;
  if (loading) return <p>Cargando PDF…</p>;
  if (error) return <p style={{ color: "red" }}>{error}</p>;

  /* estilos básicos inline */
  const wrapStyle = {
    fontFamily: "Arial, sans-serif",
    width: "100%",
    maxWidth: isMobile ? "100%" : "8.5in",
    margin: "auto",
    padding: isMobile ? "16px 20px" : "32px 48px",
    background: "#fff",
    lineHeight: 1.5,
    boxSizing: "border-box",
  };
  const tableStyle = {
    width: "100%",
    borderCollapse: "collapse",
    marginTop: 20,
    fontSize: isMobile ? 12 : 14,
  };
  const thTd = {
    border: "1px solid #000",
    padding: isMobile ? 6 : 8,
    textAlign: "left",
  };

  /* ───────── render ───────── */
  return (
    <div ref={divRef} style={wrapStyle}>
      {/* HEADER */}
      <header
        style={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 24,
        }}
      >
        <img
          src={logo}
          alt="KWSOLAR"
          style={{ maxHeight: 70, marginBottom: isMobile ? 12 : 0 }}
        />
        <h2
          style={{
            textAlign: "center",
            fontWeight: 700,
            fontSize: 20,
            flex: 1,
          }}
        >
          COTIZACIÓN&nbsp;DE&nbsp;ENERGÍA&nbsp;SOLAR
        </h2>
      </header>

      {/* DATOS DEL CLIENTE */}
      <section style={{ marginBottom: 24 }}>
        <p
          style={{
            background: "#FFE6D0",
            padding: 8,
            fontWeight: 700,
            margin: 0,
          }}
        >
          COTIZACIÓN PARA:
        </p>
        <p style={{ border: "1px solid #000", padding: 10, margin: 0 }}>
          {data.titular}
        </p>
      </section>

      {/* TABLA PRINCIPAL */}
      <table style={tableStyle}>
        <thead>
          <tr style={{ background: "#FFE6D0" }}>
            <th style={thTd}>Concepto</th>
            <th style={{ ...thTd, textAlign: "center" }}>Valor</th>
          </tr>
        </thead>
        <tbody>
          <Row label="Tipo de Tarifa CFE" value={data.tarifaCFE} />
          <Row
            label="Consumo Total Actual (kWh / año)"
            value={fmt(data.consumoTotalAnualKwh)}
          />
          <Row
            label="Pago Actual CFE (MXN / año)"
            value={fmt(data.pagoActualAnualMxn, { currency: true })}
          />
          <Row
            label="Potencia Sistema Requerida (kW)"
            value={fmt(data.potenciaSistema)}
          />
          <Row label="Número de Paneles" value={data.numPaneles} />
          <Row label="Potencia por Panel" value="585 W" />
          <Row
            label="Producción Estimada (kWh / año)"
            value={fmt(data.produccionAnualKwh)}
          />

          {/* NUEVAS FILAS */}
          <Row
            label="Valor Energía Solar Generada (MXN / año)"
            value={fmt(data.valorEnergiaSolarAnual, { currency: true })}
          />
          <Row
            label="Ahorro Anual Estimado (MXN)"
            value={fmt(data.ahorroAnualMxn, { currency: true })}
          />
          <Row
            label="Precio Sistema Solar (USD)"
            value={fmt(data.precioUsd, { currency: true })}
          />
          <Row
            label="Precio Sistema Solar (MXN)"
            value={fmt(data.precioMxn, { currency: true })}
          />
          <Row
            label="Retorno de Inversión (años)"
            value={fmt(data.retornoAnios, { currency: false })}
          />
        </tbody>
      </table>

      {/* RETORNO (leyenda) */}
      <p style={{ marginTop: 24, fontSize: 14 }}>
        Con este sistema asegurarás un&nbsp;
        <strong style={{ color: "#E06A00" }}>
          retorno de inversión estimado de {data.retornoAnios || "-"} años
        </strong>
        . Después tu energía será prácticamente gratis.
      </p>

      {/* CONTACTO */}
      <div
        style={{
          marginTop: 24,
          fontSize: 13,
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          justifyContent: "center",
          gap: isMobile ? 4 : 24,
          color: "#333",
        }}
      >
        <span>
          <strong>Teléfono:</strong>{" "}
          <a href="tel:+526621430333" style={{ color: "#E06A00" }}>
            662&nbsp;143&nbsp;0333
          </a>
        </span>
        <span>
          <strong>Correo:</strong>{" "}
          <a
            href="mailto:info@kwsolar.com"
            style={{ color: "#E06A00", textDecoration: "none" }}
          >
            info@kwsolar.com
          </a>
        </span>
      </div>

      {/* FOOTER */}
      <p
        style={{
          fontSize: 12,
          textAlign: "center",
          marginTop: 32,
          borderTop: "1px dashed #AAA",
          paddingTop: 12,
          color: "#666",
        }}
      >
        * Cotización sujeta a verificación en sitio y variación de tipo de
        cambio
      </p>

      {/* OCULTA botones internos si hideActions (PDF via modal) */}
      {!hideActions && (
        <div style={{ marginTop: 24, textAlign: "center" }}>
          <button
            onClick={() => ref?.current?.download()}
            style={{
              padding: "8px 18px",
              background: "#E06A00",
              color: "#fff",
              border: "none",
              borderRadius: 4,
              cursor: "pointer",
            }}
          >
            Descargar PDF
          </button>
        </div>
      )}
    </div>
  );
});

export default SolarProposalReceipt;
